import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { CampoControlado, Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { anunciarOcultos } from '../../componentes/sensivel';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { nomeDaEmpresa, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { Selo } from '../../componentes/base';
import { api, ErroRequisicao, get, getTodos, mensagemDeErro } from '../../nucleo/api';
import { dinheiro, numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { ofereceNa } from './vertical-item';
import { RECORRENCIAS, type ItemOportunidade, type Oportunidade, type Recorrencia } from './tipos';

interface Linha {
  chave: string;
  servico_id: string;
  descricao: string;
  quantidade: string;
  preco: number | null;
  vertical_id: string;
  recorrencia: Recorrencia;
  /** CRM lote 4: o desconto vem e volta como estava (este editor não o mostra; o da ficha da negociação, sim). */
  desconto: Pick<ItemOportunidade, 'desconto_centavos' | 'desconto_tipo' | 'desconto_percentual'>;
}

const linhaDe = (i: ItemOportunidade): Linha => ({
  chave: crypto.randomUUID(),
  servico_id: i.servico_id ?? '',
  descricao: i.descricao,
  quantidade: String(i.quantidade).replace('.', ','),
  preco: i.preco_centavos,
  vertical_id: i.vertical_id ?? '',
  recorrencia: i.recorrencia,
  desconto: { desconto_centavos: i.desconto_centavos ?? 0, desconto_tipo: i.desconto_tipo ?? 'valor', desconto_percentual: i.desconto_percentual ?? null },
});
/** Review r46 visual (B2): a quantidade (até 4 casas) no formato do idioma, com as casas que ela tem. */
export const quantidadeNoIdioma = (q: number) => numero(q, Math.min(4, (String(q).split('.')[1] ?? '').length));
const nova = (recorrencia: Recorrencia = 'mensal'): Linha => ({ chave: crypto.randomUUID(), servico_id: '', descricao: '', quantidade: '1', preco: null, vertical_id: '', recorrencia, desconto: { desconto_centavos: 0, desconto_tipo: 'valor', desconto_percentual: null } });
const quantidadeDe = (q: string) => {
  const n = Number(q.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Trilha 4 (ciclo do cliente): itens da oportunidade (serviço do catálogo, descrição, quantidade, preço e a recorrência). O
 * valor da oportunidade vira a soma; no ganho, o contrato nasce com eles. Item 15 (21/10): a recorrência é de cada item
 * (itens mistos são aceitos); 22/10: o ganho cria um contrato por empresa, com a recorrência de cada item. Aberta e com `pipeline.editar`,
 * edita; senão, só a lista. Mesmos componentes do editor de itens do contrato.
 * V3 verticais: com mais de uma vertical possível (as da oportunidade, ou todas as visíveis se ela é de "Todas"), cada item
 * escolhe a dele e a lista de serviços mostra só os oferecidos nela; no ganho, um contrato por empresa.
 */
export function ItensOportunidade({ op, podeEditar, aoSalvar }: { op: Oportunidade; podeEditar: boolean; aoSalvar: () => Promise<void> | void }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const toast = useToast();
  const itens = op.itens ?? [];
  const [linhas, setLinhas] = useState<Linha[]>(() => itens.map(linhaDe));
  const editavel = podeEditar && op.estado === 'aberta';
  const catalogo = useEmpresasDoCatalogo();
  const empresas = catalogo.data?.empresas ?? [];
  const marcacao = useQuery({
    queryKey: [`/verticais/pipeline.oportunidade/${op.id}`],
    queryFn: () => get<{ empresas: string[]; todas: boolean }>(`/verticais/pipeline.oportunidade/${op.id}`),
    enabled: empresas.length > 1,
  });
  const opcoesVertical = empresas.length > 1 && marcacao.data ? (marcacao.data.todas ? empresas : empresas.filter((e) => marcacao.data!.empresas.includes(e.id))) : [];
  const comVertical = opcoesVertical.length > 1;
  const nomeVertical = (id: string | null | undefined) => {
    const e = id && empresas.length > 1 ? empresas.find((x) => x.id === id) : null;
    return e ? nomeDaEmpresa(e) : null;
  };
  const servicos = useQuery({
    queryKey: ['pipeline', 'servicos'],
    queryFn: () => getTodos<{ id: string; nome: string; preco_venda_centavos: number; empresas?: string[] }>('/cadastros/servicos', { uso: 'venda', ativo: true }).then((r) => r.itens),
    enabled: editavel && s.pode('cadastros.ver'),
    staleTime: 30_000,
  });
  const salvar = useMutation({
    mutationFn: () => {
      const corpo = linhas
        .filter((l) => l.descricao.trim() || l.preco !== null || l.servico_id)
        .map((l) => {
          const q = quantidadeDe(l.quantidade);
          if (!l.descricao.trim() || l.preco === null || q <= 0) throw new Error(t('itens.incompleto'));
          return { servico_id: l.servico_id || null, descricao: l.descricao.trim(), quantidade: q, preco_centavos: l.preco, recorrencia: l.recorrencia, vertical_id: l.vertical_id || null, ...l.desconto };
        });
      return api('PUT', `/pipeline/oportunidades/${op.id}/itens`, { itens: corpo });
    },
    onSuccess: async () => {
      toast.sucesso(t('itens.salvos'));
      await aoSalvar();
    },
    onError: (e) => toast.erro(e instanceof ErroRequisicao || !(e instanceof Error) ? mensagemDeErro(e) : e.message),
  });
  const muda = (chave: string, p: Partial<Linha>) => setLinhas((ls) => ls.map((l) => (l.chave === chave ? { ...l, ...p } : l)));
  const total = linhas.reduce((soma, l) => soma + Math.round(quantidadeDe(l.quantidade) * (l.preco ?? 0)) - (l.desconto.desconto_tipo === 'valor' ? (l.desconto.desconto_centavos ?? 0) : 0), 0);

  // Item 15: com uma recorrência só, ela segue no título; com várias, cada item mostra a dele.
  const unica = new Set(itens.map((i) => i.recorrencia)).size === 1 ? itens[0]!.recorrencia : null;
  if (!editavel) {
    if (!itens.length) return null;
    return (
      <section className="flex flex-col gap-2" aria-labelledby={`itens-${op.id}`}>
        <h3 id={`itens-${op.id}`} className="text-sm font-semibold">
          {t('itens.titulo')}
          {unica && ` · ${t(`itens.recorrencia.${unica}`)}`}
        </h3>
        <ul className="divide-y divide-borda rounded-lg border border-borda">
          {itens.map((i) => (
            <li key={i.id} className="flex min-w-0 flex-wrap justify-between gap-x-4 px-3 py-2 text-sm">
              <TextoCortado className="min-w-0 flex-1">{textoLivre(i.descricao, op.parceiro_nome)}</TextoCortado>
              {nomeVertical(i.vertical_id) && <Selo cor="ciano">{nomeVertical(i.vertical_id)}</Selo>}
              {!unica && <Selo>{t(`itens.recorrencia.${i.recorrencia}`)}</Selo>}
              <span className="numero text-tinta-2">
                {quantidadeNoIdioma(i.quantidade)} × {anunciarOcultos(dinheiro(i.preco_centavos, op.moeda))}
              </span>
            </li>
          ))}
        </ul>
      </section>
    );
  }
  return (
    <section className="flex flex-col gap-3" aria-labelledby={`itens-${op.id}`}>
      <h3 id={`itens-${op.id}`} className="text-sm font-semibold">
        {t('itens.titulo')}
      </h3>
      <p className="text-xs text-tinta-3">{t('itens.ajuda')}</p>
      {!linhas.length && <p className="text-sm text-tinta-3">{t('itens.vazio')}</p>}
      {linhas.map((l, n) => (
        <div key={l.chave} className="grid min-w-0 grid-cols-6 gap-2 rounded-lg border border-borda bg-sup-1 p-3">
          {comVertical && (
            <div className="col-span-6">
              {/* 22/10 (Gustavo): com mais de uma vertical possível, a do item é obrigatória (o servidor recusa sem ela). */}
              <Rotulo id={`it-vt-${l.chave}`} rotulo={t('itens.vertical')} obrigatorio>
                <Selecao
                  id={`it-vt-${l.chave}`}
                  vazio=""
                  required
                  opcoes={[
                    ...opcoesVertical.map((e) => ({ valor: e.id, rotulo: nomeDaEmpresa(e) })),
                    ...(l.vertical_id && !opcoesVertical.some((e) => e.id === l.vertical_id) ? [{ valor: l.vertical_id, rotulo: nomeVertical(l.vertical_id) ?? t('verticais.outra', { ns: 'nucleo' }) }] : []),
                  ]}
                  value={l.vertical_id}
                  onChange={(e) => {
                    const sv = servicos.data?.find((x) => x.id === l.servico_id);
                    muda(l.chave, { vertical_id: e.target.value, ...(sv && !ofereceNa(sv, e.target.value) ? { servico_id: '' } : {}) });
                  }}
                />
              </Rotulo>
            </div>
          )}
          {s.pode('cadastros.ver') && (
            <div className="col-span-6">
              <Rotulo id={`it-sv-${l.chave}`} rotulo={t('itens.servico')}>
                <Selecao
                  id={`it-sv-${l.chave}`}
                  vazio={t('itens.semServico')}
                  opcoes={(servicos.data ?? []).filter((x) => x.id === l.servico_id || ofereceNa(x, l.vertical_id)).map((x) => ({ valor: x.id, rotulo: x.nome }))}
                  value={l.servico_id}
                  onChange={(e) => {
                    const sv = servicos.data?.find((x) => x.id === e.target.value);
                    muda(l.chave, sv ? { servico_id: sv.id, descricao: l.descricao.trim() ? l.descricao : sv.nome, preco: l.preco ?? sv.preco_venda_centavos } : { servico_id: '' });
                  }}
                />
              </Rotulo>
            </div>
          )}
          <div className="col-span-6">
            <Rotulo id={`it-desc-${l.chave}`} rotulo={t('itens.descricao', { n: n + 1 })}>
              <EntradaSensivel mascara={null} livre id={`it-desc-${l.chave}`} maxLength={300} value={l.descricao} onChange={(e) => muda(l.chave, { descricao: e.target.value })} />
            </Rotulo>
          </div>
          <div className="col-span-6 sm:col-span-2">
            <Rotulo id={`it-rec-${l.chave}`} rotulo={t('itens.recorrenciaCampo')}>
              <Selecao id={`it-rec-${l.chave}`} opcoes={RECORRENCIAS.map((r) => ({ valor: r, rotulo: t(`itens.recorrencia.${r}`) }))} value={l.recorrencia} onChange={(e) => muda(l.chave, { recorrencia: e.target.value as Recorrencia })} />
            </Rotulo>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Rotulo id={`it-qtd-${l.chave}`} rotulo={t('itens.quantidade')}>
              <Entrada id={`it-qtd-${l.chave}`} inputMode="decimal" className="numero text-right" value={l.quantidade} onChange={(e) => muda(l.chave, { quantidade: e.target.value })} />
            </Rotulo>
          </div>
          <div className="col-span-3 sm:col-span-2">
            <CampoControlado def={{ nome: `it-preco-${l.chave}`, rotulo: t('itens.preco'), tipo: 'dinheiro' }} valor={l.preco} aoMudar={(v) => muda(l.chave, { preco: v as number | null })} />
          </div>
          <div className="col-span-1 flex items-end justify-end">
            <Botao variante="fantasma" aria-label={t('itens.remover')} title={t('itens.remover')} onClick={() => setLinhas((ls) => ls.filter((x) => x.chave !== l.chave))} icone={<Trash2 className="size-4" />} />
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Botao icone={<Plus className="size-4" />} onClick={() => setLinhas((ls) => [...ls, nova(ls[ls.length - 1]?.recorrencia)])}>
          {t('itens.adicionar')}
        </Botao>
        <Botao carregando={salvar.isPending} onClick={() => salvar.mutate()}>
          {t('itens.salvar')}
        </Botao>
        <span className="ml-auto text-sm text-tinta-2">
          {t('itens.total')} <span className="numero font-semibold text-tinta-1">{anunciarOcultos(dinheiro(total, op.moeda))}</span>
        </span>
      </div>
    </section>
  );
}
